/**
 * The operations dashboard's (COO/CFO view) presentation logic: ranges, number formats, the P&L
 * row layout, KPI selection, plan-vs-actual tone and the cost form's validation.
 *
 * JSX-free like the rest of `src/lib`, because the dashboard's Jest runs in `node` without jsdom —
 * everything here is what can be tested; the pages only draw it. All arithmetic was done by the
 * API (`apps/api/src/app/internal-admin/operations/`); this file never recomputes a business
 * figure, it only picks, orders and formats them.
 */
import type {
  OpsCostItemInputDto,
  OpsFinanceReportDto,
  OpsPnlMonthDto,
  OpsSaasMonthDto,
  OpsUnitEconomicsMonthDto,
} from '../../../../libs/api-client/src/generated/models';
import {
  OpsFinanceCostCadence,
  OpsFinanceCostCategory,
  OpsFinanceCurrency,
  OpsFinancePlanLine,
} from '../../../../libs/api-client/src/generated/models';
import { intlLocale } from '../i18n/formats';
import type { SupportedLocale } from '../i18n';

/** The report route. */
export const OPERATIONS_PATH = '/operations';

/**
 * The dashboard's settings sub-page (costs, plan, cash, rates, Partner sync). No sidebar entry of
 * its own: it is reached from the dashboard's header button and its deep links (`?tab=`).
 */
export const OPERATIONS_SETTINGS_PATH = '/operations/settings';

// ── Months & ranges ─────────────────────────────────────────────────────────

/** `YYYY-MM`. */
export type MonthKey = string;

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Whether `value` is a `YYYY-MM` month. */
export function isMonthKey(value: string): boolean {
  return MONTH.test(value);
}

/** The month `date` falls into, in Berlin time — the zone the API cuts months in. */
export function monthOfDate(date: Date): MonthKey {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(date);
  const y = parts.find((p) => p.type === 'year')?.value ?? '1970';
  const m = parts.find((p) => p.type === 'month')?.value ?? '01';
  return `${y}-${m}`;
}

/** The calendar day `date` falls on, in Berlin time, as `YYYY-MM-DD`. */
export function dayOfDate(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** `month` shifted by `delta` months. */
export function shiftMonth(month: MonthKey, delta: number): MonthKey {
  const [y, m] = month.split('-').map(Number);
  const index = y * 12 + (m - 1) + delta;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

/** The range presets the page offers. `quarter` is one calendar quarter, picked from a list. */
export type RangePreset =
  | 'last12'
  | 'quarter'
  | 'ytd'
  | 'lastYear'
  | 'last24'
  | 'custom';

/** Every preset, in display order. */
export const RANGE_PRESETS: readonly RangePreset[] = [
  'last12',
  'quarter',
  'ytd',
  'lastYear',
  'last24',
  'custom',
];

/** A resolved range. */
export interface MonthRange {
  readonly from: MonthKey;
  readonly to: MonthKey;
}

/** A calendar quarter, `YYYY-Qn`. */
export type QuarterKey = string;

const QUARTER = /^(\d{4})-Q([1-4])$/;

/** The quarter `month` falls into. */
export function quarterOf(month: MonthKey): QuarterKey {
  const [y, m] = month.split('-').map(Number);
  return `${y}-Q${Math.floor((m - 1) / 3) + 1}`;
}

/** The three months of `quarter`; a malformed key falls back to the quarter of `fallback`. */
export function quarterRange(
  quarter: QuarterKey,
  fallback: MonthKey,
): MonthRange {
  const match = QUARTER.exec(quarter) ?? QUARTER.exec(quarterOf(fallback));
  const year = match?.[1] ?? fallback.slice(0, 4);
  const first = (Number(match?.[2] ?? 1) - 1) * 3 + 1;
  const from = `${year}-${String(first).padStart(2, '0')}`;
  return { from, to: shiftMonth(from, 2) };
}

/** The running quarter and the `count − 1` before it, newest first — the quarter picker's list. */
export function recentQuarters(current: MonthKey, count: number): QuarterKey[] {
  const start = quarterRange(quarterOf(current), current).from;
  return Array.from({ length: count }, (_, i) =>
    quarterOf(shiftMonth(start, -3 * i)),
  );
}

/** "Q4 2026" — the same in both locales. */
export function formatQuarter(quarter: QuarterKey): string {
  const match = QUARTER.exec(quarter);
  return match ? `Q${match[2]} ${match[1]}` : quarter;
}

/**
 * The range a preset stands for, relative to the running month. `custom` keeps `fallback`;
 * `quarter` is the picked quarter (the running one when none is given).
 */
export function presetRange(
  preset: RangePreset,
  current: MonthKey,
  fallback: MonthRange,
  quarter: QuarterKey = quarterOf(current),
): MonthRange {
  const year = current.slice(0, 4);
  switch (preset) {
    case 'last12':
      return { from: shiftMonth(current, -11), to: current };
    case 'quarter':
      return quarterRange(quarter, current);
    case 'ytd':
      return { from: `${year}-01`, to: current };
    case 'lastYear': {
      const last = String(Number(year) - 1);
      return { from: `${last}-01`, to: `${last}-12` };
    }
    case 'last24':
      return { from: shiftMonth(current, -23), to: current };
    case 'custom':
      return fallback;
  }
}

/** A month label for column heads: "Okt 26" / "Oct 26". */
export function formatMonthShort(
  month: MonthKey,
  locale: SupportedLocale,
): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString(
    intlLocale(locale),
    {
      month: 'short',
      year: '2-digit',
      timeZone: 'UTC',
    },
  );
}

/** A month label in prose: "Oktober 2026". */
export function formatMonthLong(
  month: MonthKey,
  locale: SupportedLocale,
): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString(
    intlLocale(locale),
    {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    },
  );
}

// ── Numbers ─────────────────────────────────────────────────────────────────

/**
 * Money. Whole units by default — a P&L read across twelve columns is about magnitude, and cents
 * in every cell make it unreadable; the cost list passes `cents` because there the exact amount
 * on the invoice is the point.
 */
export function formatMoney(
  value: number | null | undefined,
  locale: SupportedLocale,
  options: { readonly currency?: string; readonly cents?: boolean } = {},
): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return '—';
  const digits = options.cents ? 2 : 0;
  // Rounded first and `|| 0`, so a negated zero or −0,40 € never prints as "−0 €".
  const shown = Number(value.toFixed(digits)) || 0;
  return shown.toLocaleString(intlLocale(locale), {
    style: 'currency',
    currency: options.currency ?? 'EUR',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** A compact money figure for chart axes: "12 Tsd. €" / "€12K". */
export function formatMoneyCompact(
  value: number,
  locale: SupportedLocale,
): string {
  return value.toLocaleString(intlLocale(locale), {
    style: 'currency',
    currency: 'EUR',
    notation: 'compact',
    maximumFractionDigits: 1,
  });
}

/** A fraction as a percentage with one decimal ("12,3 %" / "12.3%"); null is a dash. */
export function formatPercent(
  ratio: number | null | undefined,
  locale: SupportedLocale,
): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio))
    return '—';
  return ratio.toLocaleString(intlLocale(locale), {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

/** A plain decimal with one digit ("3,4"), null is a dash. */
export function formatDecimal(
  value: number | null | undefined,
  locale: SupportedLocale,
): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return '—';
  return value.toLocaleString(intlLocale(locale), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

/** A count, null is a dash. */
export function formatInt(
  value: number | null | undefined,
  locale: SupportedLocale,
): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return '—';
  return value.toLocaleString(intlLocale(locale));
}

/** Change from `previous` to `current` as a fraction; null when there is no base. */
export function relativeChange(
  current: number,
  previous: number | null | undefined,
): number | null {
  if (previous === null || previous === undefined || previous === 0)
    return null;
  return (current - previous) / Math.abs(previous);
}

// ── The report ──────────────────────────────────────────────────────────────

/** Index of the newest COMPLETE month, or the newest month when none is complete; -1 when empty. */
export function lastCompleteIndex(
  pnl: readonly Pick<OpsPnlMonthDto, 'isPartial'>[],
): number {
  for (let i = pnl.length - 1; i >= 0; i--) if (!pnl[i].isPartial) return i;
  return pnl.length - 1;
}

/**
 * Where the shops stood for the selected range. A range that reaches the running month reads the
 * LIVE funnel ("now"); a range that ended in the past reads its last month's month-end stocks,
 * because "now" next to a chart of last year would show shops and MRR that period never had.
 */
export interface RangeFunnel {
  /** True: the live funnel. False: the stocks at the end of `month`. */
  readonly live: boolean;
  /** The month whose end the figures describe; null when live (or the report has no month). */
  readonly month: MonthKey | null;
  readonly installed: number;
  readonly trialing: number;
  readonly paying: number;
  readonly frozen: number;
  readonly unconverted: number;
  readonly installToPaying: number | null;
  /** EUR. */
  readonly mrr: number;
  readonly mrrUsd: number;
}

/** The funnel for the report's range — see {@link RangeFunnel}. */
export function rangeFunnel(
  report: Pick<OpsFinanceReportDto, 'to' | 'currentMonth' | 'saas' | 'funnel'>,
): RangeFunnel {
  const end = report.saas[report.saas.length - 1];
  if (report.to >= report.currentMonth || !end) {
    const f = report.funnel;
    return {
      live: true,
      month: null,
      installed: f.installed,
      trialing: f.trialing,
      paying: f.paying,
      frozen: f.frozen,
      unconverted: f.unconverted,
      installToPaying: f.installToPaying,
      mrr: f.mrr,
      mrrUsd: f.mrrUsd,
    };
  }
  return {
    live: false,
    month: end.month,
    installed: end.installedEnd,
    trialing: end.trialingEnd,
    paying: end.payingEnd,
    frozen: end.frozenEnd,
    unconverted: end.unconvertedEnd,
    installToPaying: end.installToPaying,
    mrr: end.mrr,
    mrrUsd: end.mrrUsd,
  };
}

/** One KPI tile. `delta` compares with the month before; `goodWhenUp` colours it. */
export interface Kpi {
  readonly id:
    | 'mrr'
    | 'arr'
    | 'paying'
    | 'installToPaying'
    | 'revenueNet'
    | 'ebitda'
    | 'logoChurn'
    | 'ltvToCac'
    | 'runway';
  readonly value: string;
  /** Short context under the value, already formatted. */
  readonly sub: string | null;
  readonly delta: number | null;
  readonly goodWhenUp: boolean;
}

/** The month figures a KPI tile reads. */
interface MonthTriple {
  readonly pnl: OpsPnlMonthDto | undefined;
  readonly saas: OpsSaasMonthDto | undefined;
  readonly ue: OpsUnitEconomicsMonthDto | undefined;
}

function monthAt(report: OpsFinanceReportDto, index: number): MonthTriple {
  return {
    pnl: report.pnl[index],
    saas: report.saas[index],
    ue: report.unitEconomics[index],
  };
}

/**
 * The KPI row. Stock figures (MRR, ARR, paying shops, install → paying) come from
 * {@link rangeFunnel}: "now" when the range reaches the running month (compared with the last
 * complete month), otherwise the end of the range's last month (compared with the month before
 * it). Money figures (net revenue, EBITDA) are the last COMPLETE month, because the running month
 * is month-to-date and would always read as a collapse.
 *
 * @param labels - Pre-translated fragments (`margin`, `ofInstalled`, `cashPositive`, `inMonth`,
 *   `monthToDate`, `endOfMonth`, `months`, `noBalance`) — passed in so this stays free of the i18n runtime.
 */
export function buildKpis(
  report: OpsFinanceReportDto,
  locale: SupportedLocale,
  labels: {
    readonly margin: (pct: string) => string;
    readonly ofInstalled: (installed: string) => string;
    readonly cashPositive: string;
    readonly inMonth: (month: string) => string;
    /** For a running month — the fallback when the range has no complete month yet. */
    readonly monthToDate: (month: string) => string;
    readonly endOfMonth: (month: string) => string;
    readonly months: (n: string) => string;
    readonly noBalance: string;
  },
): Kpi[] {
  const i = lastCompleteIndex(report.pnl);
  const last = monthAt(report, i);
  const prev = monthAt(report, i - 1);
  const monthLabel = last.pnl
    ? (last.pnl.isPartial ? labels.monthToDate : labels.inMonth)(
        formatMonthLong(last.pnl.month, locale),
      )
    : null;
  const runway = report.runway;
  const funnel = rangeFunnel(report);
  // What the stock tiles compare with: live → the last complete month; a past range → the month
  // before its last one.
  const base = funnel.live ? last.saas : report.saas[report.saas.length - 2];
  const asOf =
    funnel.month === null
      ? null
      : labels.endOfMonth(formatMonthLong(funnel.month, locale));
  return [
    {
      id: 'mrr',
      value: formatMoney(funnel.mrr, locale),
      sub: formatMoney(funnel.mrrUsd, locale, { currency: 'USD' }),
      delta: relativeChange(funnel.mrr, base?.mrr),
      goodWhenUp: true,
    },
    {
      id: 'arr',
      value: formatMoney(funnel.mrr * 12, locale),
      sub: asOf,
      delta: relativeChange(funnel.mrr, base?.mrr),
      goodWhenUp: true,
    },
    {
      id: 'paying',
      value: formatInt(funnel.paying, locale),
      sub: labels.ofInstalled(formatInt(funnel.installed, locale)),
      delta: relativeChange(funnel.paying, base?.payingEnd),
      goodWhenUp: true,
    },
    {
      id: 'installToPaying',
      value: formatPercent(funnel.installToPaying, locale),
      sub: asOf,
      delta: null,
      goodWhenUp: true,
    },
    {
      id: 'revenueNet',
      value: formatMoney(last.pnl?.revenueNet, locale),
      sub: monthLabel,
      delta: last.pnl
        ? relativeChange(last.pnl.revenueNet, prev.pnl?.revenueNet)
        : null,
      goodWhenUp: true,
    },
    {
      id: 'ebitda',
      value: formatMoney(last.pnl?.ebitda, locale),
      sub: last.pnl
        ? labels.margin(formatPercent(last.pnl.ebitdaMargin, locale))
        : null,
      delta: last.pnl
        ? relativeChange(last.pnl.ebitda, prev.pnl?.ebitda)
        : null,
      goodWhenUp: true,
    },
    {
      id: 'logoChurn',
      value: formatPercent(last.saas?.logoChurn, locale),
      sub: monthLabel,
      delta: null,
      goodWhenUp: false,
    },
    {
      id: 'ltvToCac',
      value: formatDecimal(last.ue?.ltvToCac, locale),
      sub: null,
      delta: null,
      goodWhenUp: true,
    },
    {
      id: 'runway',
      value:
        runway.runwayMonths !== null
          ? labels.months(formatDecimal(runway.runwayMonths, locale))
          : runway.cashPositive
            ? labels.cashPositive
            : '—',
      sub:
        runway.cashBalance !== null
          ? formatMoney(runway.cashBalance, locale)
          : labels.noBalance,
      delta: null,
      goodWhenUp: true,
    },
  ];
}

/** A numeric P&L field. */
export type PnlField =
  | 'subscriptionRevenue'
  | 'usageRevenue'
  | 'otherRevenue'
  | 'revenueGross'
  | 'refunds'
  | 'shopifyFees'
  | 'revenueNet'
  | 'cogs'
  | 'grossProfit'
  | 'opex'
  | 'ebitda'
  | 'depreciation'
  | 'ebit'
  | 'interest'
  | 'ebt'
  | 'taxes'
  | 'netIncome';

/** How a P&L row is drawn. */
export type PnlRowKind =
  | 'detail'
  | 'line'
  | 'subtotal'
  | 'total'
  | 'margin'
  | 'category';

/** One row of the P&L table. */
export interface PnlRow {
  readonly id: string;
  readonly kind: PnlRowKind;
  /** Catalogue key of the label (a category row carries the category instead). */
  readonly labelKey: string;
  /** A money field, a margin field, or a cost category. */
  readonly value:
    | {
        readonly type: 'field';
        readonly field: PnlField;
        readonly negate: boolean;
      }
    | {
        readonly type: 'margin';
        readonly field: 'grossMargin' | 'ebitdaMargin';
      }
    | { readonly type: 'category'; readonly category: OpsFinanceCostCategory };
}

const COGS_CATEGORIES: readonly OpsFinanceCostCategory[] = [
  OpsFinanceCostCategory.Hosting,
  OpsFinanceCostCategory.ThirdPartyServices,
];
const OPEX_CATEGORIES: readonly OpsFinanceCostCategory[] = [
  OpsFinanceCostCategory.Personnel,
  OpsFinanceCostCategory.Freelance,
  OpsFinanceCostCategory.Marketing,
  OpsFinanceCostCategory.SoftwareTools,
  OpsFinanceCostCategory.OfficeAdmin,
  OpsFinanceCostCategory.OtherOpex,
];

const field = (
  id: string,
  kind: PnlRowKind,
  labelKey: string,
  f: PnlField,
  negate = false,
): PnlRow => ({
  id,
  kind,
  labelKey,
  value: { type: 'field', field: f, negate },
});
const category = (c: OpsFinanceCostCategory): PnlRow => ({
  id: `cat-${c}`,
  kind: 'category',
  labelKey: '',
  value: { type: 'category', category: c },
});

/**
 * The P&L, top to bottom. Costs are shown NEGATIVE (as they reduce the result) so every column
 * adds up as read; `negate` marks the rows whose API value is a positive cost.
 *
 * @param expanded - Show the per-category rows under COGS and OpEx.
 */
export function pnlRows(expanded: boolean): PnlRow[] {
  const rows: PnlRow[] = [
    field(
      'sub',
      'detail',
      'operations.pnl.subscriptionRevenue',
      'subscriptionRevenue',
    ),
    field('usage', 'detail', 'operations.pnl.usageRevenue', 'usageRevenue'),
    field('other', 'detail', 'operations.pnl.otherRevenue', 'otherRevenue'),
    field('gross', 'line', 'operations.pnl.revenueGross', 'revenueGross'),
    field('refunds', 'line', 'operations.pnl.refunds', 'refunds'),
    field('fees', 'line', 'operations.pnl.shopifyFees', 'shopifyFees', true),
    field('net', 'subtotal', 'operations.pnl.revenueNet', 'revenueNet'),
    field('cogs', 'line', 'operations.pnl.cogs', 'cogs', true),
  ];
  if (expanded) rows.push(...COGS_CATEGORIES.map(category));
  rows.push(
    field('gp', 'subtotal', 'operations.pnl.grossProfit', 'grossProfit'),
    {
      id: 'gm',
      kind: 'margin',
      labelKey: 'operations.pnl.grossMargin',
      value: { type: 'margin', field: 'grossMargin' },
    },
    field('opex', 'line', 'operations.pnl.opex', 'opex', true),
  );
  if (expanded) rows.push(...OPEX_CATEGORIES.map(category));
  rows.push(
    field('ebitda', 'total', 'operations.pnl.ebitda', 'ebitda'),
    {
      id: 'em',
      kind: 'margin',
      labelKey: 'operations.pnl.ebitdaMargin',
      value: { type: 'margin', field: 'ebitdaMargin' },
    },
    field('da', 'line', 'operations.pnl.depreciation', 'depreciation', true),
    field('ebit', 'subtotal', 'operations.pnl.ebit', 'ebit'),
    field('interest', 'line', 'operations.pnl.interest', 'interest', true),
    field('ebt', 'subtotal', 'operations.pnl.ebt', 'ebt'),
    field('taxes', 'line', 'operations.pnl.taxes', 'taxes', true),
    field('ni', 'total', 'operations.pnl.netIncome', 'netIncome'),
  );
  return rows;
}

/** The number a P&L row shows for one month (costs negated; margins as fractions). */
export function pnlCell(row: PnlRow, month: OpsPnlMonthDto): number | null {
  const v = row.value;
  if (v.type === 'margin') return month[v.field];
  if (v.type === 'category') {
    const hit = month.costsByCategory.find((c) => c.category === v.category);
    return hit ? -hit.amount : 0;
  }
  const raw = month[v.field];
  return v.negate ? -raw : raw;
}

/** A row's total over the range: money sums; a margin is recomputed from the summed figures. */
export function pnlTotal(
  row: PnlRow,
  months: readonly OpsPnlMonthDto[],
): number | null {
  const v = row.value;
  if (v.type === 'margin') {
    const net = months.reduce((t, m) => t + m.revenueNet, 0);
    if (net <= 0) return null;
    const numerator = months.reduce(
      (t, m) => t + (v.field === 'grossMargin' ? m.grossProfit : m.ebitda),
      0,
    );
    return numerator / net;
  }
  return months.reduce((t, m) => t + (pnlCell(row, m) ?? 0), 0);
}

// ── Plan vs. actual ─────────────────────────────────────────────────────────

/** Whether a variance is good, bad or neutral: revenue wants to be above plan, costs below. */
export function varianceTone(
  line: string,
  variance: number,
): 'good' | 'bad' | 'neutral' {
  if (variance === 0) return 'neutral';
  const above = variance > 0;
  return line === OpsFinancePlanLine.Revenue
    ? above
      ? 'good'
      : 'bad'
    : above
      ? 'bad'
      : 'good';
}

// ── Cohorts ─────────────────────────────────────────────────────────────────

/** Retention share, or null for an empty cohort. */
export function cohortShare(count: number, size: number): number | null {
  return size === 0 ? null : count / size;
}

/** A heat bucket for a retention share, for the cohort grid's cell tint. */
export function heatBucket(share: number | null): 0 | 1 | 2 | 3 | 4 {
  if (share === null) return 0;
  if (share >= 0.8) return 4;
  if (share >= 0.6) return 3;
  if (share >= 0.4) return 2;
  return 1;
}

// ── The cost form ───────────────────────────────────────────────────────────

/** The cost form as typed. */
export interface CostDraft {
  readonly name: string;
  readonly category: OpsFinanceCostCategory;
  readonly cadence: OpsFinanceCostCadence;
  readonly amount: string;
  readonly currency: OpsFinanceCurrency;
  readonly startMonth: string;
  readonly endMonth: string;
  readonly vendor: string;
  readonly note: string;
}

/** Which field is wrong, as a catalogue key. */
export type CostDraftErrors = Partial<Record<keyof CostDraft, string>>;

/** The fields of a cost item that decide when it books. */
export interface CostWindow {
  readonly id: string;
  readonly name: string;
  readonly cadence: OpsFinanceCostCadence;
  readonly startMonth: string;
  readonly endMonth?: string | null;
}

/**
 * Items that book in the same month as another item of the same name — the trace of a price
 * change entered as "end the old one (inclusive), add the new one from this month", which books
 * BOTH amounts in the handover month. Item id → the first month both book in.
 */
export function costOverlaps(
  items: readonly CostWindow[],
): Map<string, MonthKey> {
  const span = (c: CostWindow): [string, string] => [
    c.startMonth,
    c.cadence === OpsFinanceCostCadence.OneOff
      ? c.startMonth
      : (c.endMonth ?? '9999-12'),
  ];
  const key = (c: CostWindow): string => c.name.trim().toLocaleLowerCase();
  const out = new Map<string, MonthKey>();
  for (const a of items) {
    for (const b of items) {
      if (a.id === b.id || key(a) !== key(b)) continue;
      const [aStart, aEnd] = span(a);
      const [bStart, bEnd] = span(b);
      if (aStart > bEnd || bStart > aEnd) continue;
      const first = aStart > bStart ? aStart : bStart;
      const known = out.get(a.id);
      if (!known || first < known) out.set(a.id, first);
    }
  }
  return out;
}

/** A blank form starting in the running month. */
export function emptyCostDraft(current: MonthKey): CostDraft {
  return {
    name: '',
    category: OpsFinanceCostCategory.SoftwareTools,
    cadence: OpsFinanceCostCadence.Monthly,
    amount: '',
    currency: OpsFinanceCurrency.Eur,
    startMonth: current,
    endMonth: '',
    vendor: '',
    note: '',
  };
}

/**
 * Reads an amount as a German or English operator types it: `1.234,56`, `1234,56`, `1,234.56`,
 * `1234.56`, `-39,90`. Null when it is not a number with at most two decimals.
 */
export function parseAmount(input: string): number | null {
  const s = input.replace(/\s|€|\$/g, '');
  if (s === '') return null;
  let normalised: string;
  // A thousands group never follows a leading 0: `0,005` is three decimals, not 5.
  if (/^-?[1-9]\d{0,2}(\.\d{3})+(,\d{1,2})?$/.test(s))
    normalised = s.replace(/\./g, '').replace(',', '.');
  else if (/^-?[1-9]\d{0,2}(,\d{3})+(\.\d{1,2})?$/.test(s))
    normalised = s.replace(/,/g, '');
  else if (/^-?\d+(,\d{1,2})?$/.test(s)) normalised = s.replace(',', '.');
  else if (/^-?\d+(\.\d{1,2})?$/.test(s)) normalised = s;
  else return null;
  const n = Number(normalised);
  return Number.isFinite(n) ? n : null;
}

/** Validates the form; empty object = valid. Mirrors the API's rules. */
export function validateCostDraft(draft: CostDraft): CostDraftErrors {
  const errors: CostDraftErrors = {};
  if (draft.name.trim().length === 0)
    errors.name = 'operations.costs.errors.name';
  const amount = parseAmount(draft.amount);
  if (amount === null) errors.amount = 'operations.costs.errors.amount';
  else if (amount === 0) errors.amount = 'operations.costs.errors.amountZero';
  if (!isMonthKey(draft.startMonth))
    errors.startMonth = 'operations.costs.errors.month';
  if (draft.cadence !== OpsFinanceCostCadence.OneOff && draft.endMonth !== '') {
    if (!isMonthKey(draft.endMonth))
      errors.endMonth = 'operations.costs.errors.month';
    else if (
      isMonthKey(draft.startMonth) &&
      draft.endMonth < draft.startMonth
    ) {
      errors.endMonth = 'operations.costs.errors.endBeforeStart';
    }
  }
  return errors;
}

/** The request body for a valid draft. Call only after {@link validateCostDraft} passed. */
export function costDraftToInput(draft: CostDraft): OpsCostItemInputDto {
  return {
    name: draft.name.trim(),
    category: draft.category,
    cadence: draft.cadence,
    amount: parseAmount(draft.amount) ?? 0,
    currency: draft.currency,
    startMonth: draft.startMonth,
    endMonth:
      draft.cadence === OpsFinanceCostCadence.OneOff || draft.endMonth === ''
        ? null
        : draft.endMonth,
    vendor: draft.vendor.trim(),
    note: draft.note.trim(),
  };
}

/** Every cost category, in P&L order. */
export const COST_CATEGORIES: readonly OpsFinanceCostCategory[] = [
  ...COGS_CATEGORIES,
  ...OPEX_CATEGORIES,
  OpsFinanceCostCategory.Depreciation,
  OpsFinanceCostCategory.Interest,
  OpsFinanceCostCategory.Taxes,
];

/** Every plan line: the revenue target first, then the costs in P&L order. */
export const PLAN_LINES: readonly OpsFinancePlanLine[] = [
  OpsFinancePlanLine.Revenue,
  OpsFinancePlanLine.Hosting,
  OpsFinancePlanLine.ThirdPartyServices,
  OpsFinancePlanLine.Personnel,
  OpsFinancePlanLine.Freelance,
  OpsFinancePlanLine.Marketing,
  OpsFinancePlanLine.SoftwareTools,
  OpsFinancePlanLine.OfficeAdmin,
  OpsFinancePlanLine.OtherOpex,
  OpsFinancePlanLine.Depreciation,
  OpsFinancePlanLine.Interest,
  OpsFinancePlanLine.Taxes,
];
